/**
 * CumulativeDeltaPrimitive
 *
 * Renders cumulative delta as a line overlay on the price chart.
 * Shows net buying/selling pressure over time with direction-based coloring:
 * - Green segments when delta is increasing (buying pressure)
 * - Red segments when delta is decreasing (selling pressure)
 * - Dashed zero reference line for context
 */

import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import { DeltaData } from '../../types/volumeProfile';

// ============================================================================
// Configuration
// ============================================================================

export interface CumulativeDeltaPrimitiveOptions {
  /** Line width in pixels */
  lineWidth: number;
  /** Positive delta color (buying pressure / increasing) */
  positiveColor: string;
  /** Negative delta color (selling pressure / decreasing) */
  negativeColor: string;
  /** Zero line color */
  zeroLineColor: string;
  /** Opacity of the line (0-1) */
  opacity: number;
  /** Show zero reference line */
  showZeroLine: boolean;
}

const DEFAULT_OPTIONS: CumulativeDeltaPrimitiveOptions = {
  lineWidth: 2,
  positiveColor: '#26A69A',  // Green for buying pressure
  negativeColor: '#EF5350',  // Red for selling pressure
  zeroLineColor: '#666666',
  opacity: 0.8,
  showZeroLine: true,
};

// ============================================================================
// Renderer Data (screen coordinates)
// ============================================================================

interface DeltaPointRendererData {
  x: number;
  y: number;
  cumulativeDelta: number;
}

// ============================================================================
// Renderer
// ============================================================================

class CumulativeDeltaPaneRenderer implements IPrimitivePaneRenderer {
  private _data: DeltaPointRendererData[] = [];
  private _enabled: boolean = true;
  private _options: CumulativeDeltaPrimitiveOptions = DEFAULT_OPTIONS;
  private _zeroY: number | null = null;
  private _chartHeight: number = 0;

  update(
    data: DeltaPointRendererData[],
    enabled: boolean,
    options: CumulativeDeltaPrimitiveOptions,
    zeroY: number | null,
    chartHeight: number
  ): void {
    this._data = data;
    this._enabled = enabled;
    this._options = options;
    this._zeroY = zeroY;
    this._chartHeight = chartHeight;
  }

  draw(target: any): void {
    if (!this._enabled || this._data.length < 2) return;

    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const verticalPixelRatio = scope.verticalPixelRatio || pixelRatio;

      ctx.save();

      // Draw zero reference line first (behind delta line)
      if (this._options.showZeroLine && this._zeroY !== null) {
        const zeroY = this._zeroY * verticalPixelRatio;
        ctx.strokeStyle = this._options.zeroLineColor;
        ctx.lineWidth = 1 * pixelRatio;
        ctx.setLineDash([4 * pixelRatio, 4 * pixelRatio]);
        ctx.beginPath();
        ctx.moveTo(0, zeroY);
        ctx.lineTo(scope.bitmapSize.width, zeroY);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draw delta line with direction-based coloring
      ctx.lineWidth = this._options.lineWidth * pixelRatio;
      ctx.globalAlpha = this._options.opacity;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // Draw line segments with color based on direction
      for (let i = 1; i < this._data.length; i++) {
        const prev = this._data[i - 1];
        const curr = this._data[i];

        const x1 = prev.x * pixelRatio;
        const x2 = curr.x * pixelRatio;
        const y1 = prev.y * verticalPixelRatio;
        const y2 = curr.y * verticalPixelRatio;

        // Skip segments where either point is off-screen
        if (x1 < 0 && x2 < 0) continue;
        if (x1 > scope.bitmapSize.width && x2 > scope.bitmapSize.width) continue;

        // Color based on delta direction (is it increasing or decreasing?)
        const isIncreasing = curr.cumulativeDelta >= prev.cumulativeDelta;
        ctx.strokeStyle = isIncreasing
          ? this._options.positiveColor
          : this._options.negativeColor;

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      ctx.restore();
    });
  }
}

// ============================================================================
// Pane View
// ============================================================================

class CumulativeDeltaPaneView implements IPrimitivePaneView {
  private _source: CumulativeDeltaPrimitive;
  private _renderer: CumulativeDeltaPaneRenderer;

  constructor(source: CumulativeDeltaPrimitive) {
    this._source = source;
    this._renderer = new CumulativeDeltaPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    // 'top' renders above candles and other primitives
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    const { data, zeroY, chartHeight } = this._source.getRendererData();
    this._renderer.update(
      data,
      this._source.isEnabled(),
      this._source.getOptions(),
      zeroY,
      chartHeight
    );
    return this._renderer;
  }
}

// ============================================================================
// Primitive
// ============================================================================

export class CumulativeDeltaPrimitive implements ISeriesPrimitive<Time> {
  private _data: DeltaData[] = [];
  private _paneView: CumulativeDeltaPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _enabled: boolean = true;
  private _options: CumulativeDeltaPrimitiveOptions = { ...DEFAULT_OPTIONS };

  constructor(options: Partial<CumulativeDeltaPrimitiveOptions> = {}) {
    this._options = { ...DEFAULT_OPTIONS, ...options };
    this._paneView = new CumulativeDeltaPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  // -------------------------------------------------------------------------
  // Data Methods
  // -------------------------------------------------------------------------

  setData(data: DeltaData[]): void {
    this._data = data;
    this._requestUpdate?.();
  }

  appendData(delta: DeltaData): void {
    this._data.push(delta);
    // Keep last 1000 points (matching MAX_DELTA_POINTS_PER_SYMBOL)
    if (this._data.length > 1000) {
      this._data = this._data.slice(-1000);
    }
    this._requestUpdate?.();
  }

  getData(): DeltaData[] {
    return this._data;
  }

  // -------------------------------------------------------------------------
  // State Methods
  // -------------------------------------------------------------------------

  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this._requestUpdate?.();
  }

  isEnabled(): boolean {
    return this._enabled;
  }

  setOptions(options: Partial<CumulativeDeltaPrimitiveOptions>): void {
    this._options = { ...this._options, ...options };
    this._requestUpdate?.();
  }

  getOptions(): CumulativeDeltaPrimitiveOptions {
    return this._options;
  }

  // -------------------------------------------------------------------------
  // Renderer Data Calculation
  // -------------------------------------------------------------------------

  getRendererData(): {
    data: DeltaPointRendererData[];
    zeroY: number | null;
    chartHeight: number;
  } {
    if (!this._series || !this._chart || !this._enabled || this._data.length < 2) {
      return { data: [], zeroY: null, chartHeight: 0 };
    }

    const timeScale = this._chart.timeScale();
    const result: DeltaPointRendererData[] = [];

    // Get chart dimensions for Y-axis scaling
    // We need to scale delta values to fit the visible chart area
    const chartHeight = (this._chart as any).options().height || 600;

    // Find min/max delta for Y-axis scaling
    const deltas = this._data.map((d) => d.cumulativeDelta);
    const minDelta = Math.min(...deltas, 0);
    const maxDelta = Math.max(...deltas, 0);
    const deltaRange = maxDelta - minDelta || 1;

    // Scale delta to chart Y coordinate
    // Use 80% of chart height, centered with 10% margin top/bottom
    const marginRatio = 0.1;
    const usableHeight = chartHeight * (1 - 2 * marginRatio);
    const topMargin = chartHeight * marginRatio;

    const deltaToY = (delta: number): number => {
      const normalized = (maxDelta - delta) / deltaRange;
      return topMargin + normalized * usableHeight;
    };

    // Calculate zero line Y position
    const zeroY = deltaToY(0);

    // Convert data points to screen coordinates
    for (const point of this._data) {
      // Convert timestamp to Time format (seconds)
      const timeInSeconds = Math.floor(point.timestamp / 1000) as Time;

      // Get x coordinate from time
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      // Get y coordinate from delta value
      const y = deltaToY(point.cumulativeDelta);

      result.push({
        x,
        y,
        cumulativeDelta: point.cumulativeDelta,
      });
    }

    return { data: result, zeroY, chartHeight };
  }
}

// ============================================================================
// Factory Function
// ============================================================================

export function createCumulativeDeltaPrimitive(
  options?: Partial<CumulativeDeltaPrimitiveOptions>
): CumulativeDeltaPrimitive {
  return new CumulativeDeltaPrimitive(options);
}
